/**
 * Structural validation for content packs.
 *
 * Written by hand rather than pulled from a schema library on purpose. This is the tool Deshret
 * will be staring at while authoring, so the messages matter more than the brevity of the
 * implementation: every problem names the exact path (`signals[3].placement.frequencyKhz`), says
 * what was expected, and shows what it found. A generic "expected number, received string at
 * /signals/3/placement/frequencyKhz" is a worse authoring experience, and this also keeps the
 * validator usable in the browser without adding a dependency to the client bundle.
 *
 * Cross-cutting checks that need the engine (band collisions over time, asset existence on disk)
 * live in the CLI validator — this module covers everything decidable from the JSON alone.
 */
import { TRANSMISSION_PRESETS } from '../audio/transmission.js';
import {
  PACK_SCHEMA_VERSION,
  SUPPORTED_SCHEMA_VERSIONS,
  type ContentPack,
  type LocalizedText,
  type PackSignal,
} from './schema.js';

export type IssueSeverity = 'error' | 'warning';

export interface ValidationIssue {
  severity: IssueSeverity;
  /** Dotted path into the pack, e.g. `signals[2].schedule.gameHours[0].fromHour`. */
  path: string;
  message: string;
}

export interface ValidationResult {
  ok: boolean;
  errors: ValidationIssue[];
  warnings: ValidationIssue[];
  /** Present only when the pack is structurally sound enough to use. */
  pack?: ContentPack;
}

const SIGNAL_KINDS = new Set(['story', 'parasite', 'npc', 'ghost']);
const ID_PATTERN = /^[a-z0-9][a-z0-9-]*$/;

class Collector {
  readonly errors: ValidationIssue[] = [];
  readonly warnings: ValidationIssue[] = [];

  error(path: string, message: string): void {
    this.errors.push({ severity: 'error', path, message });
  }

  warn(path: string, message: string): void {
    this.warnings.push({ severity: 'warning', path, message });
  }
}

function typeName(value: unknown): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  return typeof value;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function requireNumber(
  collector: Collector,
  value: unknown,
  path: string,
  options: { min?: number; max?: number; integer?: boolean } = {},
): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    collector.error(path, `expected a number, found ${typeName(value)}`);
    return undefined;
  }
  if (options.integer && !Number.isInteger(value)) {
    collector.error(path, `expected a whole number, found ${value}`);
    return undefined;
  }
  if (options.min !== undefined && value < options.min) {
    collector.error(path, `must be at least ${options.min}, found ${value}`);
    return undefined;
  }
  if (options.max !== undefined && value > options.max) {
    collector.error(path, `must be at most ${options.max}, found ${value}`);
    return undefined;
  }
  return value;
}

/** A number that may be absent. Absent is fine; present and wrong is not. */
function requireOptionalNumber(
  collector: Collector,
  value: unknown,
  path: string,
  options: { min?: number; max?: number; integer?: boolean } = {},
): number | undefined {
  if (value === undefined) return undefined;
  return requireNumber(collector, value, path, options);
}

function requireId(collector: Collector, value: unknown, path: string): string | undefined {
  if (typeof value !== 'string' || value.length === 0) {
    collector.error(path, `expected a non-empty id string, found ${typeName(value)}`);
    return undefined;
  }
  if (!ID_PATTERN.test(value)) {
    collector.error(
      path,
      `ids must be lowercase letters, digits and hyphens (it becomes a folder name for audio assets); found "${value}"`,
    );
    return undefined;
  }
  return value;
}

function checkLocalized(
  collector: Collector,
  value: unknown,
  path: string,
  locales: string[],
  defaultLocale: string,
  required: boolean,
): void {
  if (value === undefined) {
    if (required) collector.error(path, 'is required');
    return;
  }
  if (!isObject(value)) {
    collector.error(
      path,
      `expected a locale map like { "${defaultLocale}": "..." }, found ${typeName(value)}`,
    );
    return;
  }

  const text = value as LocalizedText;
  if (typeof text[defaultLocale] !== 'string' || text[defaultLocale]!.length === 0) {
    collector.error(path, `must contain the pack's default locale "${defaultLocale}"`);
  }

  for (const [tag, entry] of Object.entries(text)) {
    if (typeof entry !== 'string') {
      collector.error(`${path}.${tag}`, `expected a string, found ${typeName(entry)}`);
    }
    if (!locales.includes(tag)) {
      collector.warn(
        `${path}.${tag}`,
        `locale "${tag}" is not listed in the pack's "locales" — it will never be shown`,
      );
    }
  }

  for (const tag of locales) {
    if (tag !== defaultLocale && typeof text[tag] !== 'string') {
      collector.warn(`${path}.${tag}`, `not translated yet (falls back to "${defaultLocale}")`);
    }
  }
}

function checkHour(collector: Collector, value: unknown, path: string): void {
  requireNumber(collector, value, path, { min: 0, max: 23, integer: true });
}

function checkSchedule(collector: Collector, schedule: unknown, path: string): void {
  if (!isObject(schedule)) {
    collector.error(path, `expected an object, found ${typeName(schedule)}`);
    return;
  }

  const { gameHours, gameDays, gameSpan, real } = schedule;

  if (gameHours !== undefined) {
    if (!Array.isArray(gameHours)) {
      collector.error(`${path}.gameHours`, `expected an array, found ${typeName(gameHours)}`);
    } else if (gameHours.length === 0) {
      collector.warn(
        `${path}.gameHours`,
        'is empty, so it constrains nothing — remove it or add a window',
      );
    } else {
      gameHours.forEach((window, index) => {
        const windowPath = `${path}.gameHours[${index}]`;
        if (!isObject(window)) {
          collector.error(windowPath, `expected an object, found ${typeName(window)}`);
          return;
        }
        checkHour(collector, window.fromHour, `${windowPath}.fromHour`);
        checkHour(collector, window.toHour, `${windowPath}.toHour`);
        if (window.fromHour === window.toHour) {
          collector.warn(
            windowPath,
            'fromHour equals toHour, which means always on — if that is the intent, drop gameHours entirely',
          );
        }
      });
    }
  }

  if (gameDays !== undefined) {
    if (!Array.isArray(gameDays)) {
      collector.error(`${path}.gameDays`, `expected an array, found ${typeName(gameDays)}`);
    } else {
      gameDays.forEach((day, index) => {
        requireNumber(collector, day, `${path}.gameDays[${index}]`, { min: 1, integer: true });
      });
    }
  }

  if (gameSpan !== undefined) {
    const spanPath = `${path}.gameSpan`;
    if (!isObject(gameSpan)) {
      collector.error(spanPath, `expected an object, found ${typeName(gameSpan)}`);
    } else {
      const from = requireNumber(collector, gameSpan.fromGameMs, `${spanPath}.fromGameMs`);
      const to = requireNumber(collector, gameSpan.toGameMs, `${spanPath}.toGameMs`);
      if (from !== undefined && to !== undefined && to <= from) {
        collector.error(spanPath, `toGameMs (${to}) must be after fromGameMs (${from})`);
      }
    }
  }

  if (real !== undefined) {
    const realPath = `${path}.real`;
    if (!isObject(real)) {
      collector.error(realPath, `expected an object, found ${typeName(real)}`);
      return;
    }
    if (real.hours !== undefined) {
      if (!isObject(real.hours)) {
        collector.error(`${realPath}.hours`, `expected an object, found ${typeName(real.hours)}`);
      } else {
        checkHour(collector, real.hours.from, `${realPath}.hours.from`);
        checkHour(collector, real.hours.to, `${realPath}.hours.to`);
      }
    }
    if (real.weekdays !== undefined) {
      if (!Array.isArray(real.weekdays)) {
        collector.error(
          `${realPath}.weekdays`,
          `expected an array, found ${typeName(real.weekdays)}`,
        );
      } else {
        real.weekdays.forEach((day, index) => {
          requireNumber(collector, day, `${realPath}.weekdays[${index}]`, {
            min: 0,
            max: 6,
            integer: true,
          });
        });
      }
    }
    if (real.dateRange !== undefined) {
      const rangePath = `${realPath}.dateRange`;
      if (!isObject(real.dateRange)) {
        collector.error(rangePath, `expected an object, found ${typeName(real.dateRange)}`);
      } else {
        for (const key of ['fromIso', 'toIso'] as const) {
          const value = real.dateRange[key];
          if (typeof value !== 'string' || Number.isNaN(Date.parse(value))) {
            collector.error(
              `${rangePath}.${key}`,
              `expected an ISO 8601 timestamp, found ${JSON.stringify(value)}`,
            );
          }
        }
      }
    }
  }
}

function checkSource(
  collector: Collector,
  source: unknown,
  path: string,
  npcIds: Set<string>,
  locales: string[],
): void {
  if (!isObject(source)) {
    collector.error(path, `expected an object, found ${typeName(source)}`);
    return;
  }

  if (source.type === 'synth') {
    if (typeof source.preset !== 'string' || source.preset.length === 0) {
      collector.error(`${path}.preset`, 'a synth source needs a preset name');
    } else if (!TRANSMISSION_PRESETS.includes(source.preset)) {
      // A typo used to fall through to a carrier at runtime with only a console warning, which
      // means an author hears *something* and never learns their parasite signal is not the one
      // they wrote. Chapter 7 adds a lot of these, so a misspelling is now a build failure.
      collector.error(
        `${path}.preset`,
        `unknown preset "${source.preset}" — available: ${TRANSMISSION_PRESETS.join(', ')}`,
      );
    }
    if (source.params !== undefined) {
      if (!isObject(source.params)) {
        collector.error(`${path}.params`, `expected an object, found ${typeName(source.params)}`);
      } else {
        for (const [key, value] of Object.entries(source.params)) {
          requireNumber(collector, value, `${path}.params.${key}`);
        }
      }
    }
    return;
  }

  if (source.type === 'audio') {
    const npc = requireId(collector, source.npc, `${path}.npc`);
    requireId(collector, source.node, `${path}.node`);
    if (npc !== undefined && !npcIds.has(npc)) {
      collector.error(
        `${path}.npc`,
        `references npc "${npc}", which is not defined in this pack's "npcs"`,
      );
    }
    if (source.locales !== undefined) {
      if (!Array.isArray(source.locales)) {
        collector.error(`${path}.locales`, `expected an array, found ${typeName(source.locales)}`);
      } else {
        source.locales.forEach((tag, index) => {
          if (typeof tag !== 'string') {
            collector.error(`${path}.locales[${index}]`, `expected a locale tag string`);
          } else if (!locales.includes(tag)) {
            collector.warn(
              `${path}.locales[${index}]`,
              `locale "${tag}" is not listed in the pack's "locales"`,
            );
          }
        });
      }
    }
    return;
  }

  collector.error(
    `${path}.type`,
    `expected "synth" or "audio", found ${JSON.stringify(source.type)}`,
  );
}

function checkSignal(
  collector: Collector,
  signal: unknown,
  path: string,
  context: {
    locales: string[];
    defaultLocale: string;
    npcIds: Set<string>;
    band?: { minKhz: number; maxKhz: number };
    /** Present once the story layer has been read, so signals can gate on its clues and events. */
    story?: StoryContext;
  },
): string | undefined {
  if (!isObject(signal)) {
    collector.error(path, `expected an object, found ${typeName(signal)}`);
    return undefined;
  }

  const id = requireId(collector, signal.id, `${path}.id`);

  if (typeof signal.kind !== 'string' || !SIGNAL_KINDS.has(signal.kind)) {
    collector.error(
      `${path}.kind`,
      `expected one of ${[...SIGNAL_KINDS].join(', ')}, found ${JSON.stringify(signal.kind)}`,
    );
  }

  checkLocalized(
    collector,
    signal.label,
    `${path}.label`,
    context.locales,
    context.defaultLocale,
    false,
  );

  if (signal.kind === 'npc') {
    const npc = requireId(collector, signal.npc, `${path}.npc`);
    if (npc !== undefined && !context.npcIds.has(npc)) {
      collector.error(`${path}.npc`, `references npc "${npc}", which is not defined in "npcs"`);
    }
  } else if (signal.npc !== undefined) {
    const npc = requireId(collector, signal.npc, `${path}.npc`);
    if (npc !== undefined && !context.npcIds.has(npc)) {
      collector.error(`${path}.npc`, `references npc "${npc}", which is not defined in "npcs"`);
    }
  }

  const placementPath = `${path}.placement`;
  if (!isObject(signal.placement)) {
    collector.error(placementPath, `expected an object, found ${typeName(signal.placement)}`);
  } else {
    const frequency = requireNumber(
      collector,
      signal.placement.frequencyKhz,
      `${placementPath}.frequencyKhz`,
      { min: 0 },
    );
    const width = requireNumber(collector, signal.placement.widthKhz, `${placementPath}.widthKhz`, {
      min: 0.1,
    });

    if (frequency !== undefined && context.band) {
      if (frequency < context.band.minKhz || frequency > context.band.maxKhz) {
        collector.error(
          `${placementPath}.frequencyKhz`,
          `${frequency} kHz is outside the pack's band (${context.band.minKhz}-${context.band.maxKhz} kHz), so it can never be tuned`,
        );
      }
    }
    if (width !== undefined && width > 500) {
      collector.warn(
        `${placementPath}.widthKhz`,
        `${width} kHz is very wide — it will be audible across a large slice of the band and is likely to collide with everything`,
      );
    }

    if (signal.placement.drift !== undefined) {
      const driftPath = `${placementPath}.drift`;
      if (!isObject(signal.placement.drift)) {
        collector.error(driftPath, `expected an object, found ${typeName(signal.placement.drift)}`);
      } else {
        const drift = signal.placement.drift;
        requireNumber(collector, drift.khzPerGameHour, `${driftPath}.khzPerGameHour`);
        if (drift.anchor !== undefined && drift.anchor !== 'window' && drift.anchor !== 'world') {
          collector.error(
            `${driftPath}.anchor`,
            `expected "window" or "world", found ${JSON.stringify(drift.anchor)}`,
          );
        }
        const min =
          drift.minKhz === undefined
            ? undefined
            : requireNumber(collector, drift.minKhz, `${driftPath}.minKhz`);
        const max =
          drift.maxKhz === undefined
            ? undefined
            : requireNumber(collector, drift.maxKhz, `${driftPath}.maxKhz`);
        if (min !== undefined && max !== undefined && max <= min) {
          collector.error(driftPath, `maxKhz (${max}) must be above minKhz (${min})`);
        }
        if (drift.khzPerGameHour !== 0 && min === undefined && max === undefined) {
          collector.warn(
            driftPath,
            'drifts without bounds — over a long night it will wander off the band and become unfindable',
          );
        }
      }
    }
  }

  checkSchedule(collector, signal.schedule, `${path}.schedule`);

  if (signal.priority !== undefined) {
    requireNumber(collector, signal.priority, `${path}.priority`, { integer: true });
  }

  checkSource(collector, signal.source, `${path}.source`, context.npcIds, context.locales);

  /* --- v2 story hooks --------------------------------------------------------------------- */
  if (context.story) {
    checkGate(collector, signal.requires, `${path}.requires`, context.story);
    checkEffects(collector, signal.effects, `${path}.effects`, context.story);

    if (signal.listenSeconds !== undefined) {
      const seconds = requireNumber(collector, signal.listenSeconds, `${path}.listenSeconds`, {
        min: 0,
      });
      if (seconds !== undefined && seconds > 120) {
        collector.warn(
          `${path}.listenSeconds`,
          `${seconds}s is a long time to hold one frequency — a player will assume nothing is there and tune away`,
        );
      }
    }

    if (signal.dialogue !== undefined) {
      const dialogue = requireId(collector, signal.dialogue, `${path}.dialogue`);
      if (dialogue !== undefined && !context.story.dialogueIds.has(dialogue)) {
        collector.error(
          `${path}.dialogue`,
          `carries dialogue "${dialogue}", which is not defined in story.dialogues`,
        );
      }
    }
  } else {
    for (const key of ['requires', 'effects', 'listenSeconds', 'dialogue'] as const) {
      if (signal[key] !== undefined) {
        collector.error(
          `${path}.${key}`,
          `is a story field, but this pack has no "story" block for it to refer to`,
        );
      }
    }
  }

  return id;
}

/* ---- v2: the story layer ------------------------------------------------------------------ */

interface StoryContext {
  locales: string[];
  defaultLocale: string;
  npcIds: Set<string>;
  clueIds: Set<string>;
  eventIds: Set<string>;
  threadIds: Set<string>;
  dialogueIds: Set<string>;
}

/**
 * Check a gate's shape and that every id it names exists.
 *
 * A gate referencing a clue that no effect ever grants is the single most common way to write an
 * unwinnable mystery, so it is an error rather than a warning — the reachability pass would catch
 * the consequence, but naming the typo is far more useful than reporting the symptom.
 */
function checkGate(collector: Collector, gate: unknown, path: string, context: StoryContext): void {
  if (gate === undefined) return;
  if (!isObject(gate)) {
    collector.error(path, `expected an object, found ${typeName(gate)}`);
    return;
  }

  const clauses: Array<[string, Set<string>, string]> = [
    ['clues', context.clueIds, 'clue'],
    ['anyClues', context.clueIds, 'clue'],
    ['notClues', context.clueIds, 'clue'],
    ['events', context.eventIds, 'event'],
    ['notEvents', context.eventIds, 'event'],
  ];

  for (const [key, known, noun] of clauses) {
    const value = gate[key];
    if (value === undefined) continue;
    if (!Array.isArray(value)) {
      collector.error(`${path}.${key}`, `expected an array, found ${typeName(value)}`);
      continue;
    }
    value.forEach((id, index) => {
      const entryPath = `${path}.${key}[${index}]`;
      const parsed = requireId(collector, id, entryPath);
      if (parsed === undefined) return;
      if (!known.has(parsed)) {
        collector.error(
          entryPath,
          noun === 'clue'
            ? `requires clue "${parsed}", which no clue in this story defines`
            : `requires event "${parsed}", which nothing in this story ever fires`,
        );
      }
    });
  }

  const known = new Set(['clues', 'anyClues', 'notClues', 'events', 'notEvents']);
  for (const key of Object.keys(gate)) {
    if (!known.has(key)) {
      collector.warn(`${path}.${key}`, `unknown gate clause — it will be ignored`);
    }
  }
}

/** Check an effects block. Unknown clue ids here are a typo that silently grants nothing. */
function checkEffects(
  collector: Collector,
  effects: unknown,
  path: string,
  context: StoryContext,
): void {
  if (effects === undefined) return;
  if (!isObject(effects)) {
    collector.error(path, `expected an object, found ${typeName(effects)}`);
    return;
  }

  const clues = effects.clues;
  if (clues !== undefined) {
    if (!Array.isArray(clues)) {
      collector.error(`${path}.clues`, `expected an array, found ${typeName(clues)}`);
    } else {
      clues.forEach((id, index) => {
        const parsed = requireId(collector, id, `${path}.clues[${index}]`);
        if (parsed !== undefined && !context.clueIds.has(parsed)) {
          collector.error(
            `${path}.clues[${index}]`,
            `grants clue "${parsed}", which is not defined in story.clues`,
          );
        }
      });
    }
  }

  const events = effects.events;
  if (events !== undefined) {
    if (!Array.isArray(events)) {
      collector.error(`${path}.events`, `expected an array, found ${typeName(events)}`);
    } else {
      events.forEach((id, index) => requireId(collector, id, `${path}.events[${index}]`));
    }
  }
}

function checkDialogueOption(
  collector: Collector,
  option: unknown,
  path: string,
  context: StoryContext,
  nodeIds: Set<string>,
): string | undefined {
  if (!isObject(option)) {
    collector.error(path, `expected an object, found ${typeName(option)}`);
    return undefined;
  }

  const id = requireId(collector, option.id, `${path}.id`);
  checkLocalized(
    collector,
    option.label,
    `${path}.label`,
    context.locales,
    context.defaultLocale,
    true,
  );

  if (option.intents !== undefined) {
    if (!Array.isArray(option.intents)) {
      collector.error(`${path}.intents`, `expected an array, found ${typeName(option.intents)}`);
    } else {
      option.intents.forEach((intent, index) => {
        checkLocalized(
          collector,
          intent,
          `${path}.intents[${index}]`,
          context.locales,
          context.defaultLocale,
          true,
        );
      });
    }
  }

  checkGate(collector, option.requires, `${path}.requires`, context);
  checkEffects(collector, option.effects, `${path}.effects`, context);

  if (option.next !== undefined) {
    const next = requireId(collector, option.next, `${path}.next`);
    if (next !== undefined && !nodeIds.has(next)) {
      collector.error(
        `${path}.next`,
        `points at node "${next}", which does not exist in this dialogue`,
      );
    }
  }

  return id;
}

function checkDialogue(
  collector: Collector,
  dialogue: unknown,
  path: string,
  context: StoryContext,
): void {
  if (!isObject(dialogue)) {
    collector.error(path, `expected an object, found ${typeName(dialogue)}`);
    return;
  }

  requireId(collector, dialogue.id, `${path}.id`);

  if (dialogue.npc !== undefined) {
    const npc = requireId(collector, dialogue.npc, `${path}.npc`);
    if (npc !== undefined && !context.npcIds.has(npc)) {
      collector.error(`${path}.npc`, `references npc "${npc}", which is not defined in "npcs"`);
    }
  }

  if (dialogue.initiation !== undefined) {
    if (dialogue.initiation !== 'npc' && dialogue.initiation !== 'user') {
      collector.error(
        `${path}.initiation`,
        `expected "npc" or "user", found ${JSON.stringify(dialogue.initiation)}`,
      );
    }
  }

  if (!Array.isArray(dialogue.nodes) || dialogue.nodes.length === 0) {
    collector.error(`${path}.nodes`, 'a dialogue needs at least one node');
    return;
  }

  const nodeIds = new Set<string>();
  dialogue.nodes.forEach((node, index) => {
    if (!isObject(node)) return;
    const id = typeof node.id === 'string' ? node.id : undefined;
    if (id === undefined) return;
    if (nodeIds.has(id)) collector.error(`${path}.nodes[${index}].id`, `duplicate node id "${id}"`);
    nodeIds.add(id);
  });

  const entry = requireId(collector, dialogue.entry, `${path}.entry`);
  if (entry !== undefined && !nodeIds.has(entry)) {
    collector.error(
      `${path}.entry`,
      `opens on node "${entry}", which does not exist in this dialogue`,
    );
  }

  dialogue.nodes.forEach((node, index) => {
    const nodePath = `${path}.nodes[${index}]`;
    if (!isObject(node)) {
      collector.error(nodePath, `expected an object, found ${typeName(node)}`);
      return;
    }

    requireId(collector, node.id, `${nodePath}.id`);
    checkLocalized(
      collector,
      node.line,
      `${nodePath}.line`,
      context.locales,
      context.defaultLocale,
      true,
    );
    if (node.source !== undefined) {
      checkSource(collector, node.source, `${nodePath}.source`, context.npcIds, context.locales);
    }
    checkGate(collector, node.requires, `${nodePath}.requires`, context);
    checkEffects(collector, node.effects, `${nodePath}.effects`, context);

    if (node.next !== undefined) {
      const next = requireId(collector, node.next, `${nodePath}.next`);
      if (next !== undefined && !nodeIds.has(next)) {
        collector.error(
          `${nodePath}.next`,
          `points at node "${next}", which does not exist in this dialogue`,
        );
      }
    }

    if (node.options !== undefined) {
      if (!Array.isArray(node.options)) {
        collector.error(
          `${nodePath}.options`,
          `expected an array, found ${typeName(node.options)}`,
        );
      } else {
        const optionIds = new Set<string>();
        node.options.forEach((option, optionIndex) => {
          const optionId = checkDialogueOption(
            collector,
            option,
            `${nodePath}.options[${optionIndex}]`,
            context,
            nodeIds,
          );
          if (optionId !== undefined) {
            if (optionIds.has(optionId)) {
              collector.error(
                `${nodePath}.options[${optionIndex}].id`,
                `duplicate option id "${optionId}" within this node`,
              );
            }
            optionIds.add(optionId);
          }
        });
      }
    }
  });
}

/**
 * The story layer. Returns the id sets the signal checks need, since signals may gate on clues
 * and events the story declares.
 */
function checkStory(
  collector: Collector,
  story: unknown,
  locales: string[],
  defaultLocale: string,
  npcIds: Set<string>,
  signals: unknown,
): StoryContext {
  const context: StoryContext = {
    locales,
    defaultLocale,
    npcIds,
    clueIds: new Set(),
    eventIds: new Set(),
    threadIds: new Set(),
    dialogueIds: new Set(),
  };

  // Even with no story block, signals may still fire events and gate on them.
  collectSignalEventIds(signals, context.eventIds);

  if (story === undefined) return context;
  if (!isObject(story)) {
    collector.error('story', `expected an object, found ${typeName(story)}`);
    return context;
  }

  requireId(collector, story.id, 'story.id');
  checkLocalized(collector, story.title, 'story.title', locales, defaultLocale, true);
  if (story.premise !== undefined) {
    checkLocalized(collector, story.premise, 'story.premise', locales, defaultLocale, false);
  }

  // Threads and clues first: gates elsewhere reference them.
  if (story.threads !== undefined) {
    if (!Array.isArray(story.threads)) {
      collector.error('story.threads', `expected an array, found ${typeName(story.threads)}`);
    } else {
      story.threads.forEach((thread, index) => {
        const path = `story.threads[${index}]`;
        if (!isObject(thread)) {
          collector.error(path, `expected an object, found ${typeName(thread)}`);
          return;
        }
        const id = requireId(collector, thread.id, `${path}.id`);
        if (id !== undefined) {
          if (context.threadIds.has(id))
            collector.error(`${path}.id`, `duplicate thread id "${id}"`);
          context.threadIds.add(id);
        }
        checkLocalized(collector, thread.label, `${path}.label`, locales, defaultLocale, true);
      });
    }
  }

  if (story.clues !== undefined) {
    if (!Array.isArray(story.clues)) {
      collector.error('story.clues', `expected an array, found ${typeName(story.clues)}`);
    } else {
      story.clues.forEach((clue, index) => {
        const path = `story.clues[${index}]`;
        if (!isObject(clue)) {
          collector.error(path, `expected an object, found ${typeName(clue)}`);
          return;
        }
        const id = requireId(collector, clue.id, `${path}.id`);
        if (id !== undefined) {
          if (context.clueIds.has(id)) collector.error(`${path}.id`, `duplicate clue id "${id}"`);
          context.clueIds.add(id);
        }
        checkLocalized(collector, clue.label, `${path}.label`, locales, defaultLocale, true);
        checkLocalized(
          collector,
          clue.description,
          `${path}.description`,
          locales,
          defaultLocale,
          true,
        );
        if (clue.thread !== undefined) {
          const thread = requireId(collector, clue.thread, `${path}.thread`);
          if (thread !== undefined && !context.threadIds.has(thread)) {
            collector.error(
              `${path}.thread`,
              `belongs to thread "${thread}", which is not declared in story.threads`,
            );
          }
        }
      });
    }
  }

  // Every event id the pack mentions anywhere, so gates can be checked against them. Events are
  // not declared up front — they are just names things fire — so the set is discovered from
  // everything that can fire one, signals included.
  collectEventIds(story, context.eventIds);
  collectSignalEventIds(signals, context.eventIds);

  if (story.dialogues !== undefined) {
    if (!Array.isArray(story.dialogues)) {
      collector.error('story.dialogues', `expected an array, found ${typeName(story.dialogues)}`);
    } else {
      story.dialogues.forEach((dialogue, index) => {
        if (isObject(dialogue) && typeof dialogue.id === 'string') {
          if (context.dialogueIds.has(dialogue.id)) {
            collector.error(
              `story.dialogues[${index}].id`,
              `duplicate dialogue id "${dialogue.id}"`,
            );
          }
          context.dialogueIds.add(dialogue.id);
        }
      });
      story.dialogues.forEach((dialogue, index) => {
        checkDialogue(collector, dialogue, `story.dialogues[${index}]`, context);
      });
    }
  }

  if (story.endings !== undefined) {
    if (!Array.isArray(story.endings)) {
      collector.error('story.endings', `expected an array, found ${typeName(story.endings)}`);
    } else {
      if (story.endings.length === 0) {
        collector.warn('story.endings', 'is empty — this mystery has no resolution to reach');
      }
      const endingIds = new Set<string>();
      story.endings.forEach((ending, index) => {
        const path = `story.endings[${index}]`;
        if (!isObject(ending)) {
          collector.error(path, `expected an object, found ${typeName(ending)}`);
          return;
        }
        const id = requireId(collector, ending.id, `${path}.id`);
        if (id !== undefined) {
          if (endingIds.has(id)) collector.error(`${path}.id`, `duplicate ending id "${id}"`);
          endingIds.add(id);
        }
        checkLocalized(collector, ending.label, `${path}.label`, locales, defaultLocale, true);
        checkLocalized(collector, ending.text, `${path}.text`, locales, defaultLocale, true);
        if (ending.requires === undefined) {
          collector.error(
            `${path}.requires`,
            'an ending needs a gate, or it fires immediately and the mystery is over before it starts',
          );
        } else {
          checkGate(collector, ending.requires, `${path}.requires`, context);
        }
        checkEffects(collector, ending.effects, `${path}.effects`, context);
      });
    }
  } else {
    collector.warn('story', 'has no endings — a mystery with no resolution cannot be solved');
  }

  return context;
}

/** Events fired by signals, which live outside the story block. */
function collectSignalEventIds(signals: unknown, into: Set<string>): void {
  if (!Array.isArray(signals)) return;
  for (const signal of signals) {
    if (!isObject(signal) || !isObject(signal.effects)) continue;
    const events = signal.effects.events;
    if (!Array.isArray(events)) continue;
    for (const event of events) if (typeof event === 'string') into.add(event);
  }
}

/** Walk the whole story collecting every event id that anything fires. */
function collectEventIds(story: Record<string, unknown>, into: Set<string>): void {
  const addFrom = (effects: unknown): void => {
    if (!isObject(effects) || !Array.isArray(effects.events)) return;
    for (const event of effects.events) if (typeof event === 'string') into.add(event);
  };

  for (const dialogue of Array.isArray(story.dialogues) ? story.dialogues : []) {
    if (!isObject(dialogue) || !Array.isArray(dialogue.nodes)) continue;
    for (const node of dialogue.nodes) {
      if (!isObject(node)) continue;
      addFrom(node.effects);
      for (const option of Array.isArray(node.options) ? node.options : []) {
        if (isObject(option)) addFrom(option.effects);
      }
    }
  }
  for (const ending of Array.isArray(story.endings) ? story.endings : []) {
    if (isObject(ending)) addFrom(ending.effects);
  }
}

/**
 * Validate a parsed pack. Returns every problem found rather than throwing on the first, because
 * an author wants the whole list.
 */
export function validatePack(input: unknown): ValidationResult {
  const collector = new Collector();

  if (!isObject(input)) {
    collector.error('', `expected the pack to be a JSON object, found ${typeName(input)}`);
    return { ok: false, errors: collector.errors, warnings: collector.warnings };
  }

  // v1 packs (Chapter 2, band only) still load: every story field is optional, so a v1 pack is a
  // v2 pack that tells no story. Only an unknown version is an error.
  const version = input.schemaVersion;
  const versions: readonly number[] = SUPPORTED_SCHEMA_VERSIONS;
  if (typeof version !== 'number' || !versions.includes(version)) {
    collector.error(
      'schemaVersion',
      `this engine reads schema version(s) ${versions.join(', ')}, the pack declares ${JSON.stringify(version)}`,
    );
  } else if (version < PACK_SCHEMA_VERSION && input.story !== undefined) {
    collector.warn(
      'schemaVersion',
      `this pack has a "story" but declares version ${version} — bump it to ${PACK_SCHEMA_VERSION}`,
    );
  }

  requireId(collector, input.id, 'id');

  const defaultLocale =
    typeof input.defaultLocale === 'string' && input.defaultLocale.length > 0
      ? input.defaultLocale
      : undefined;
  if (!defaultLocale) {
    collector.error('defaultLocale', 'is required (the locale every string must be written in)');
  }

  let locales: string[] = [];
  if (!Array.isArray(input.locales) || input.locales.length === 0) {
    collector.error('locales', 'must list at least one locale tag');
  } else {
    locales = input.locales.filter((tag): tag is string => typeof tag === 'string');
    if (locales.length !== input.locales.length) {
      collector.error('locales', 'every entry must be a locale tag string');
    }
    if (defaultLocale && !locales.includes(defaultLocale)) {
      collector.error('locales', `must include the default locale "${defaultLocale}"`);
    }
  }

  checkLocalized(collector, input.title, 'title', locales, defaultLocale ?? 'en', true);

  let band: { minKhz: number; maxKhz: number } | undefined;
  if (!isObject(input.band)) {
    collector.error('band', `expected an object, found ${typeName(input.band)}`);
  } else {
    const min = requireNumber(collector, input.band.minKhz, 'band.minKhz', { min: 0 });
    const max = requireNumber(collector, input.band.maxKhz, 'band.maxKhz', { min: 0 });
    if (min !== undefined && max !== undefined) {
      if (max <= min) collector.error('band', `maxKhz (${max}) must be above minKhz (${min})`);
      else band = { minKhz: min, maxKhz: max };
    }
  }

  if (input.clock !== undefined) {
    if (!isObject(input.clock)) {
      collector.error('clock', `expected an object, found ${typeName(input.clock)}`);
    } else {
      if (input.clock.ratio !== undefined) {
        requireNumber(collector, input.clock.ratio, 'clock.ratio', { min: 0.01 });
      }
      if (input.clock.startGameMs !== undefined) {
        requireNumber(collector, input.clock.startGameMs, 'clock.startGameMs', { min: 0 });
      }
    }
  }

  if (input.transmit !== undefined) {
    if (!isObject(input.transmit)) {
      collector.error('transmit', `expected an object, found ${typeName(input.transmit)}`);
    } else {
      const transmit = input.transmit;
      const maxSeconds = requireOptionalNumber(
        collector,
        transmit.maxSeconds,
        'transmit.maxSeconds',
        { min: 0.5, max: 120 },
      );
      requireOptionalNumber(collector, transmit.cooldownSeconds, 'transmit.cooldownSeconds', {
        min: 0,
        max: 300,
      });
      const maxConsecutive = requireOptionalNumber(
        collector,
        transmit.maxConsecutive,
        'transmit.maxConsecutive',
        { min: 1, max: 100, integer: true },
      );
      const overheated = requireOptionalNumber(
        collector,
        transmit.overheatedSeconds,
        'transmit.overheatedSeconds',
        { min: 0, max: 600 },
      );
      requireOptionalNumber(collector, transmit.burstForgetSeconds, 'transmit.burstForgetSeconds', {
        min: 0,
        max: 3600,
      });

      if (maxSeconds !== undefined && maxSeconds < 2) {
        collector.warn(
          'transmit.maxSeconds',
          `${maxSeconds}s is shorter than most spoken answers — players will be cut off mid-sentence`,
        );
      }
      if (
        overheated !== undefined &&
        typeof transmit.cooldownSeconds === 'number' &&
        overheated <= transmit.cooldownSeconds
      ) {
        collector.warn(
          'transmit.overheatedSeconds',
          'is not longer than the ordinary cooldown, so overheating will not be felt as different',
        );
      }
      if (maxConsecutive === 1) {
        collector.warn(
          'transmit.maxConsecutive',
          'of 1 means every single transmission overheats the set — probably not intended',
        );
      }
    }
  }

  const npcIds = new Set<string>();
  if (input.npcs !== undefined) {
    if (!Array.isArray(input.npcs)) {
      collector.error('npcs', `expected an array, found ${typeName(input.npcs)}`);
    } else {
      input.npcs.forEach((npc, index) => {
        const path = `npcs[${index}]`;
        if (!isObject(npc)) {
          collector.error(path, `expected an object, found ${typeName(npc)}`);
          return;
        }
        const id = requireId(collector, npc.id, `${path}.id`);
        if (id !== undefined) {
          if (npcIds.has(id)) collector.error(`${path}.id`, `duplicate npc id "${id}"`);
          npcIds.add(id);
        }
        checkLocalized(collector, npc.name, `${path}.name`, locales, defaultLocale ?? 'en', true);
      });
    }
  }

  // The story is read before the signals, because signals gate on the clues and events it
  // declares — a signal saying `requires: { clues: ['x'] }` can only be checked once we know
  // whether clue "x" exists.
  const storyContext = checkStory(
    collector,
    input.story,
    locales,
    defaultLocale ?? 'en',
    npcIds,
    input.signals,
  );
  const hasStory = input.story !== undefined;

  const signalIds = new Set<string>();
  if (!Array.isArray(input.signals)) {
    collector.error('signals', `expected an array, found ${typeName(input.signals)}`);
  } else {
    if (input.signals.length === 0) {
      collector.warn('signals', 'is empty — this pack produces nothing but dead air');
    }
    input.signals.forEach((signal, index) => {
      const id = checkSignal(collector, signal, `signals[${index}]`, {
        locales,
        defaultLocale: defaultLocale ?? 'en',
        npcIds,
        band,
        ...(hasStory ? { story: storyContext } : {}),
      });
      if (id !== undefined) {
        if (signalIds.has(id))
          collector.error(`signals[${index}].id`, `duplicate signal id "${id}"`);
        signalIds.add(id);
      }
    });
  }

  const ok = collector.errors.length === 0;
  return {
    ok,
    errors: collector.errors,
    warnings: collector.warnings,
    pack: ok ? (input as unknown as ContentPack) : undefined,
  };
}

/** Every audio asset a pack references, as pack-relative paths. Used by the CLI's file check. */
export function referencedAssets(pack: ContentPack): Array<{
  signalId: string;
  locale: string;
  path: string;
}> {
  const assets: Array<{ signalId: string; locale: string; path: string }> = [];
  for (const signal of pack.signals as PackSignal[]) {
    if (signal.source.type !== 'audio') continue;
    const locales = signal.source.locales ?? pack.locales;
    for (const locale of locales) {
      assets.push({
        signalId: signal.id,
        locale,
        path: `audio/${locale}/${signal.source.npc}/${signal.source.node}.ogg`,
      });
    }
  }
  return assets;
}
